import { useMemo } from 'react';
import { UserBadge } from 'types/interfaces/badge';

// Non-linear categories show ALL earned badges (distinct achievements, not progressions)
const NON_LINEAR_CATEGORIES = new Set(['Special', 'One-at-a-Time']);

export function computeTopRankingBadges(userBadges: UserBadge[]): UserBadge[] {
  const byCategory = new Map<string, UserBadge[]>();
  for (const ub of userBadges) {
    const cat = ub.badge.category;
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(ub);
  }

  const result: UserBadge[] = [];

  byCategory.forEach((badges, category) => {
    if (NON_LINEAR_CATEGORIES.has(category)) {
      // Include all earned badges — these are independent achievements
      result.push(...badges);
    } else {
      // Linear progression — pick only the highest-level badge
      const highest = badges.reduce((best: UserBadge, ub: UserBadge) =>
        ub.badge.level > best.badge.level ? ub : best
      );
      result.push(highest);
    }
  });

  // Sort by level descending, then alphabetical by title within same level
  result.sort((a, b) => {
    const levelDiff = b.badge.level - a.badge.level;
    if (levelDiff !== 0) return levelDiff;
    return a.badge.title.localeCompare(b.badge.title);
  });

  return result;
}

export function useTopRankingBadges(userBadges: UserBadge[] | undefined): UserBadge[] {
  return useMemo(() => {
    if (!userBadges || userBadges.length === 0) return [];
    return computeTopRankingBadges(userBadges);
  }, [userBadges]);
}
